/**
 * AxialupLogo — inline SVG brand mark for AXIALUP OPC PVT LTD.
 *
 * Concept:
 *   • Stylized "A" with an upward chevron representing axial growth ("Axial Up")
 *   • Three ascending bars on the right side = digital ledger / growth
 *   • Rich Klein-blue gradient (#002FA7) to vibrant teal accent (#06B6D4)
 *
 * Use cases:
 *   <AxialupLogo size={40} />            // mark only
 *   <AxialupLogo size={40} withText />   // mark + wordmark
 *
 * Pure SVG → scales crisply on any device, looks sharp in dark/light theme,
 * and weighs <1 KB. Replace later with a PNG/file upload by setting `src` prop.
 */
export default function AxialupLogo({ size = 36, withText = false, srOnly = false, className = '' }) {
  const w = size;
  const h = size;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} aria-label="Axialup OPC Pvt Ltd" data-testid="axialup-logo">
      <svg
        width={w}
        height={h}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden={srOnly ? 'true' : 'false'}
      >
        <defs>
          <linearGradient id="axialup-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#002FA7" />
            <stop offset="55%" stopColor="#1E3A8A" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
          <linearGradient id="axialup-accent" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
          <filter id="axialup-shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="1" stdDeviation="0.6" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* Rounded square base */}
        <rect x="2" y="2" width="60" height="60" rx="14" ry="14" fill="url(#axialup-grad)" filter="url(#axialup-shadow)" />

        {/* Subtle inner highlight for depth */}
        <rect x="2" y="2" width="60" height="30" rx="14" ry="14" fill="white" opacity="0.06" />

        {/* Stylized "A" mark — left arm, right arm, crossbar */}
        <g stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none">
          {/* Left arm */}
          <path d="M14 50 L26 18" />
          {/* Right arm */}
          <path d="M38 18 L50 50" />
          {/* Cross bar */}
          <path d="M21 38 L43 38" opacity="0.85" />
        </g>

        {/* Upward chevron — the "Up" in Axialup */}
        <g stroke="url(#axialup-accent)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M22 28 L32 18 L42 28" />
        </g>

        {/* Three ascending bars (digital ledger / growth) bottom-right */}
        <g fill="#22D3EE">
          <rect x="46" y="44" width="3.5" height="6" rx="1" opacity="0.55" />
          <rect x="51" y="40" width="3.5" height="10" rx="1" opacity="0.75" />
          <rect x="56" y="34" width="3.5" height="16" rx="1" />
        </g>
      </svg>

      {withText && (
        <span className="flex flex-col leading-none">
          <span className="font-bold text-gray-900 text-[15px] tracking-tight" style={{ letterSpacing: '0.02em' }}>
            AXIAL<span className="text-[#0EA5E9]">UP</span>
          </span>
          <span className="text-[8px] text-gray-400 mt-0.5 uppercase tracking-[0.18em] font-semibold">
            OPC Pvt Ltd
          </span>
        </span>
      )}
    </span>
  );
}
